import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { SaveAnnualInsuranceRequest, getEENotice, saveAnnualInsurance } from 'core'

import {
  Alert,
  Button,
  DefaultDatePickerV2,
  DefaultInputV2,
  DefaultRadioGroupV2,
  Modal,
  Notification,
} from 'common/components'
import { downloadLink } from 'common/downloadFile'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

type SaveAnnualInsuranceCoverageProps = {
  planPeriodId: string
  visible?: boolean
  onSaved?: () => void
  onCancel?: () => void
}

type InitialForm = {
  coveredByAnIndividualHealthInsurancePolicy?: boolean
  policyName?: string
  providerName?: string
  providerStartDate?: string
}

const SaveAnnualInsuranceCoverageModal = ({
  planPeriodId,
  visible,
  onCancel = () => {},
  onSaved = () => {},
}: SaveAnnualInsuranceCoverageProps) => {
  const { t: translate } = useTranslation()
  const [isValidForm, setIsValidForm] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<InitialForm>({})
  const { sendQuery: sendGetEENotice, isLoading: isNoticeLoading } = useQueryRequestV2<string>({})

  const { sendCommand: sendSaveAnnualInsurance, isLoading: isAnnualInsuranceLoading } = useCommandRequestV2(
    (request: SaveAnnualInsuranceRequest) => saveAnnualInsurance(request),
  )

  const onFormSubmit = async () => {
    const result = await sendSaveAnnualInsurance({
      planPeriodId,
      policyName: initialValues.policyName,
      providerName: initialValues.providerName,
      providerStartDate: initialValues.providerStartDate,
    })

    if (result.completed) {
      onSaved()
    }
  }

  const getDocumentUrl = async () => {
    const result = await sendGetEENotice(() => getEENotice())
    if (result) {
      downloadLink(result, 'ee-notice')
    } else {
      Notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  return (
    <>
      <Modal
        visible={visible}
        title={'Tell us about your insurance'}
        okType={'primary'}
        okText={'Submit'}
        onOk={onFormSubmit}
        okButtonProps={{ disabled: !isValidForm || isAnnualInsuranceLoading, loading: isAnnualInsuranceLoading }}
        onCancel={onCancel}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={formValidator}
          onSubmit={() => {}}
          validate={(values) => {
            setIsValidForm(formValidator.isValidSync(values))
            setInitialValues(values)
            return {}
          }}
        >
          {(formik) => (
            <div className="invite_employees_content">
              <form onSubmit={formik.handleSubmit} autoComplete="off">
                <div className={'mb-16'}>
                  <p>Are you covered by an individual health insurance policy?</p>
                  <DefaultRadioGroupV2
                    name={'coveredByAnIndividualHealthInsurancePolicy'}
                    direction={'horizontal'}
                    items={[
                      { name: 'Yes', value: true },
                      { name: 'No', value: false },
                    ]}
                  ></DefaultRadioGroupV2>
                </div>

                {formik.values.coveredByAnIndividualHealthInsurancePolicy !== undefined &&
                  !formik.values.coveredByAnIndividualHealthInsurancePolicy && (
                    <>
                      <Alert
                        className={'mb-16'}
                        type={'info'}
                        showIcon
                        message={''}
                        description={
                          <>
                            <p>
                              Per federal regulations, you must be covered by a qualifying individual health insurance
                              policy that provides minimum essential coverage (MEC) in order to participate in this
                              individual coverage HRA (ICHRA).
                            </p>
                            <p>
                              You can use the ICHRA Notice provided below to access a special enrollment period (SEP)
                              through the individual insurance marketplace to purchase qualifying individual health
                              insurance coverage.
                            </p>
                          </>
                        }
                      />

                      <Button type={'primary'} ghost onClick={getDocumentUrl} loading={isNoticeLoading}>
                        <i className="fal fa-download mr-8"></i> ICHRA NOTICE
                      </Button>
                    </>
                  )}

                {formik.values.coveredByAnIndividualHealthInsurancePolicy !== undefined &&
                  formik.values.coveredByAnIndividualHealthInsurancePolicy && (
                    <>
                      <DefaultInputV2
                        name={'providerName'}
                        label={'Insurance provider name'}
                        placeholder={'Stride Health'}
                      />
                      <DefaultInputV2 name={'policyName'} label={'Policy name'} placeholder={'Silver PPO'} />
                      <DefaultDatePickerV2 name={'providerStartDate'} label={'Coverage start month'} />
                    </>
                  )}
              </form>
            </div>
          )}
        </Formik>
      </Modal>
    </>
  )
}

const formValidator = Yup.object({
  coveredByAnIndividualHealthInsurancePolicy: Yup.bool().oneOf([true]).required('Required'),
  providerName: Yup.string()
    .nullable()
    .when('coveredByAnIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
  policyName: Yup.string()
    .nullable()
    .when('coveredByAnIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
  providerStartDate: Yup.string()
    .nullable()
    .when('coveredByAnIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
})

export default SaveAnnualInsuranceCoverageModal
